/**
 * MessageReactions Component
 * Mostra le emoji reactions sotto un messaggio
 */

import { memo } from 'react';
import { tip } from '@/components/messages/ChatTooltip';
import { isMyReaction } from './reactionsView';

export interface Reaction {
  emoji: string;
  count: number;
  users: string[];  // JIDs degli utenti che hanno reagito
}

interface MessageReactionsProps {
  reactions: Reaction[];
  isOutbound?: boolean;
  /** Nome leggibile di chi ha reagito: mai gli identificativi. */
  resolveName: (jid: string) => string;
  /** Clic sull'etichetta: toglie la propria o si aggiunge. Assente se la chat non consente reazioni. */
  onToggle?: (emoji: string) => void;
}

export const MessageReactions = memo(function MessageReactions({
  reactions,
  isOutbound = false,
  resolveName,
  onToggle,
}: MessageReactionsProps) {
  if (!reactions?.length) return null;

  return (
    <div className={`absolute -bottom-[18px] z-[1] flex gap-0.5 ${isOutbound ? 'right-2' : 'left-2'}`}>
      {reactions.map((reaction) => {
        const mine = isMyReaction(reaction);
        const names = reaction.users.map(resolveName).join(', ');
        return (
          <button
            key={reaction.emoji}
            type="button"
            disabled={!onToggle}
            onClick={(e) => { e.stopPropagation(); onToggle?.(reaction.emoji); }}
            aria-label={`Reazione ${reaction.emoji} di ${names}`}
            className={`inline-flex items-center gap-0.5 h-[22px] px-1.5 rounded-full border text-[13px] shadow-sm disabled:cursor-default ${
              mine
                ? 'bg-[#E7F3FF] dark:bg-[#1D3A55] border-[#2383E2] dark:border-[#5AA9F5]'
                : 'bg-white dark:bg-[#2A3942] border-[rgba(55,53,47,0.14)] dark:border-white/15'
            } text-[#111B21] dark:text-[#E9EDEF]`}
            {...tip(mine ? `${names} (clicca per togliere)` : names)}
          >
            <span>{reaction.emoji}</span>
            {reaction.count > 1 && <span className="text-[11px] font-medium tabular-nums">{reaction.count}</span>}
          </button>
        );
      })}
    </div>
  );
});

/**
 * Aggrega reactions per emoji
 * @param rawReactions - Array di { emoji, senderJid }
 * @returns Array di Reaction aggregati
 */
export function aggregateReactions(
  rawReactions: Array<{ emoji: string; senderJid: string }>
): Reaction[] {
  const map = new Map<string, { count: number; users: string[] }>();

  for (const r of rawReactions) {
    const existing = map.get(r.emoji);
    if (existing) {
      existing.count++;
      existing.users.push(r.senderJid);
    } else {
      map.set(r.emoji, { count: 1, users: [r.senderJid] });
    }
  }

  return Array.from(map.entries()).map(([emoji, data]) => ({
    emoji,
    count: data.count,
    users: data.users,
  }));
}
